import { Component, DestroyRef, inject, OnInit, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { combineLatest, finalize, Observable, take, tap } from 'rxjs';
import { AuthorizeService } from 'src/app/auth/authorize.service';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { Permission } from 'src/app/common/Permission';
import { ProfileRoleConstants } from 'src/app/common/ProfileRole';
import { AppProfileService } from 'src/app/services/profile.service';
import { PermissionService } from 'src/app/services/permission.service';
import { CellClickEvent, CellCloseEvent, GridComponent, KENDO_GRID } from '@progress/kendo-angular-grid';
import { CallApiService } from '../../services/api-action.service';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

@Component({
    imports: [KENDO_GRID, AsyncClickDirective, GridNoRecordsComponent],
    selector: 'app-permission-table',
    templateUrl: './permission-table.component.html',
    styleUrls: ['./permission-table.component.css']
})
export class PermissionTableComponent implements OnInit {
  private permissionService = inject(PermissionService);
  private location = inject(Location);
  private profileService = inject(AppProfileService);
  private authService = inject(AuthorizeService);
  fb = inject(FormBuilder);
  private callApiService = inject(CallApiService);

  readonly customerId = input<string>();

  public permissions: Permission[];
  // Drives the grid's "no records" template so an in-flight load shows a
  // spinner instead of "Keine Daten vorhanden". Starts true because
  // permissions are fetched on init.
  public loading: boolean = true;

  public tenantMode: boolean;
  public isTenant: boolean = false;
  public isAdmin: boolean = false;
  public isCarrier: boolean = false;
  public profile: ExtendedProfile;
  public submitted = false;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    combineLatest({
      tenantMode: this.authService.$retailerMode,
      profile: this.profileService.currentProfile$,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((r) => {
        this.tenantMode = r.tenantMode;
        this.profile = r.profile;
        this.isTenant = r.profile.customer.isRetailer;
        this.isAdmin = r.profile.role == ProfileRoleConstants.RoleIdAdmin;
        this.refresh();
      });
  }

  refresh() {
    this.loading = true;
    this.permissionService
      .getByReference(this.customerId(), EntityTypeConstants.EntityTypeCustomer)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe((permissions) => {
        this.permissions = permissions;
      });
  }

  onBackClicked() {
    this.location.back();
  }

  public cellClickHandler(args: CellClickEvent) {
    const permission = args.dataItem as Permission;
    args.sender.editCell(args.rowIndex, args.columnIndex, this.createFormGroup(permission));
  }

  public cellCloseHandler(args: CellCloseEvent) {
    const permission = args.dataItem as Permission;
    permission.value = args.formGroup.get('value').value;
  }

  public cancelChanges(grid: GridComponent): void {
    grid.cancelCell();
  }

  public createFormGroup(dataItem: Permission) {
    return this.fb.group({
      name: dataItem.name,
      value: dataItem.value,
    });
  }

  // Returns a factory (needs the grid arg) so [asyncClick] guards the save.
  public saveChanges(grid: GridComponent): () => Observable<unknown> {
    return () => {
      grid.closeCell();
      grid.cancelCell();

      return this.callApiService
        .callWithToasts$(
          this.permissionService.createOrUpdateMultiple(this.permissions),
          $localize`Berechtigungen wurden gespeichert.`,
          $localize`Beim Speichern der Berechtigungen ist ein Fehler aufgetreten.`
        )
        .pipe(tap(() => this.onBackClicked()));
    };
  }
}
